import React, { useContext, useEffect, useState } from 'react';
import { Tag } from 'antd';
import { Case } from '../../../api/responses';
import {
  CasesContainer,
  CasesCount,
  CasesItemsWrapper,
  SearchWrapper,
  SubTitle,
  TagsWrapper,
  Title,
  TitleWrapper,
} from './styles';
import Icon from '../../assets/icons';
import { useGetCases } from '../../../hooks/useGetCases';
import { useGetAllTags } from '../../../hooks/useGetAllTags';
import { useInView } from 'react-intersection-observer';
import PreviewCase from '../../PreviewCase';
import { Input } from 'antd';
import { LanguageContext } from '../../../context';
import { useDebounce } from '../../../hooks/useDebounce';

type TCases = {
  caseType?: string;
  lang?: string;
  title: string;
  subTitle: string;
};

const Cases = ({ caseType, lang, title, subTitle }: TCases) => {
  const { ref, inView } = useInView();
  const [page, setPage] = useState(1);
  const [casesData, setCasesData] = useState<Case[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [isLastPosition, setIsLastPosition] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedTagsIds, setSelectedTagsIds] = useState<number[]>([]);
  const debouncedQuery = useDebounce(searchValue, 500);
  const languagesData = useContext(LanguageContext);

  const langFilter =
    lang && languagesData.length
      ? (languagesData.find((item) => item.code === lang)?.id ?? null)
      : null;

  const {
    isError: isErrorCases,
    data: casesDataQuery,
    isFetching,
    refetch,
  } = useGetCases(
    page,
    !isLastPosition,
    selectedTagsIds,
    debouncedQuery,
    langFilter,
    caseType
  );

  const { isError: isErrorTags, data: tagsData } = useGetAllTags(
    caseType,
    langFilter
  );

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    setSearchValue(newValue);
    setPage(1);
  };

  const onChangeTags = (tag: string, checked: boolean, id: number) => {
    const nextSelectedTags = checked
      ? [...selectedTags, tag]
      : selectedTags.filter((t) => t !== tag);
    const nextSelectedTagsIds = checked
      ? [...selectedTagsIds, id]
      : selectedTagsIds.filter((t) => t !== id);
    setSelectedTags(nextSelectedTags);
    setSelectedTagsIds(nextSelectedTagsIds);
    setIsFiltered(true);
    setPage(1);
  };

  useEffect(() => {
    if (!casesDataQuery && isFiltered) {
      setCasesData([]);
      return;
    }

    if (
      casesDataQuery &&
      Object.keys(casesDataQuery).length &&
      !!casesDataQuery.cases
    ) {
      const newData = casesDataQuery.cases;
      const data = page !== 1 ? [...casesData, ...newData] : newData;

      setIsFiltered(false);
      setIsLastPosition(data.length === casesDataQuery.total_count);
      setCasesData(data);
    }
  }, [casesDataQuery]);

  useEffect(() => {
    if (inView) {
      setPage((page) => page + 1);
    }
  }, [inView]);

  useEffect(() => {
    if (isFiltered) {
      refetch();
    }
  }, [isFiltered]);

  useEffect(() => {
    setPage(1);
    refetch();
  }, [debouncedQuery]);

  useEffect(() => {
    setPage(1);
    setIsFiltered(false);
    setSearchValue('');
    setIsLastPosition(false);
    setSelectedTags([]);
    setSelectedTagsIds([]);
  }, [title]);

  if (isErrorCases || isErrorTags)
    return <CasesContainer>Error</CasesContainer>;

  return (
    <CasesContainer key={title}>
      <TitleWrapper>
        <Title>{title === 'All cases' ? 'Bank of cases' : title}</Title>
        <SubTitle>{subTitle}</SubTitle>
      </TitleWrapper>
      <SearchWrapper>
        <Input
          size='large'
          placeholder='Search by title or text'
          prefix={<Icon variant='search' />}
          value={searchValue}
          onChange={onChangeSearch}
        />
      </SearchWrapper>
      <TagsWrapper>
        {!!tagsData &&
          !!tagsData.tags &&
          tagsData.tags.map<React.ReactNode>((tag) => (
            <Tag.CheckableTag
              key={tag.id}
              checked={selectedTags.includes(tag.name)}
              onChange={(checked) => onChangeTags(tag.name, checked, tag.id)}
            >
              {tag.name}
              {selectedTags.includes(tag.name) && <Icon variant='close' />}
            </Tag.CheckableTag>
          ))}
      </TagsWrapper>
      <CasesCount>
        {casesDataQuery ? casesDataQuery.total_count : casesData.length} cases
      </CasesCount>
      {!!casesData.length && (
        <CasesItemsWrapper>
          {casesData.map((item) => (
            <PreviewCase key={item.id} data={item} />
          ))}
          <div ref={ref}>{isFetching ? 'Loading...' : ''}</div>
        </CasesItemsWrapper>
      )}
    </CasesContainer>
  );
};

export default Cases;
